import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar,
  faBuilding,
  faFilter,
  faFileExport,
  faFilePdf,
  faFileCsv,
  faAnchor,
  faToolbox,
  faRulerVertical,
  faGripLines,
  faRoad,
  faShieldAlt,
  faCog,
  faChevronDown,
  faSearch,
  faCheckCircle,
  faTimesCircle,
  faClock,
  faExclamationTriangle,
  faSync,
  faTimes,
  faClipboardList,
  faCalendarAlt
} from '@fortawesome/free-solid-svg-icons';
import MainHeader from '../../shared/MainHeader/MainHeader';
import PageHeader from '../../shared/PageHeader/PageHeader';
import { getApiEndpoint } from '../../../config/api';
import { exportToCSV } from '../../../services/exportService';
import { exportToPDF } from '../../../utils/exportUtils';
import { getUserRole, isAdmin, isSuperAdmin } from '../../../utils/roleUtils';
import './Reports.css';

const ASSET_CATEGORIES = [
  { id: 'anchor-points',   label: 'Anchor Points',   icon: faAnchor,        endpoint: 'anchor-points' },
  { id: 'cradles',          label: 'Cradles',          icon: faToolbox,       endpoint: 'cradles' },
  { id: 'static-lines',    label: 'Static Lines',     icon: faGripLines,     endpoint: 'static-lines' },
  { id: 'ladders',          label: 'Ladders',          icon: faRulerVertical, endpoint: 'ladders' },
  { id: 'handrails',        label: 'Handrails',        icon: faShieldAlt,     endpoint: 'handrails' },
  { id: 'walkways',         label: 'Walkways',         icon: faRoad,          endpoint: 'walkways' },
  { id: 'fixed-equipment', label: 'Fixed Equipment',  icon: faCog,           endpoint: 'fixed-equipment' },
];

const STATUS_OPTIONS = [
  { value: 'all',     label: 'All Statuses' },
  { value: 'pass',    label: 'Pass' },
  { value: 'fail',    label: 'Fail' },
  { value: 'pending', label: 'Pending' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'unknown', label: 'Unknown' },
];

function formatDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-GB');
}

function isOverdue(nextDate) {
  if (!nextDate) return false;
  return new Date(nextDate) < new Date();
}

function isDueSoon(nextDate) {
  if (!nextDate) return false;
  const next = new Date(nextDate);
  const now = new Date();
  const thirtyDays = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  return next >= now && next <= thirtyDays;
}

export default function Reports() {
  const navigate = useNavigate();

  // Data state
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [selectedCategories, setSelectedCategories] = useState(ASSET_CATEGORIES.map(c => c.id));
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // UI state
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [expandedProperty, setExpandedProperty] = useState(null);

  // Fetch properties on mount
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await fetch(getApiEndpoint('/api/properties'), {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        if (!res.ok) throw new Error('Failed to load properties');
        const data = await res.json();
        setProperties(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching properties:', err);
        toast.error('Could not load properties');
      } finally {
        setPropertiesLoading(false);
      }
    };
    fetchProperties();
  }, []);

  // Fetch assets when property or categories change
  useEffect(() => {
    if (propertiesLoading) return;
    fetchAllAssets();
  }, [selectedProperty, selectedCategories, propertiesLoading]);

  const fetchAllAssets = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }

    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    const targetProperties = selectedProperty === 'all'
      ? properties
      : properties.filter(p => String(p.id) === String(selectedProperty));

    const allAssets = [];

    for (const prop of targetProperties) {
      for (const cat of ASSET_CATEGORIES) {
        if (!selectedCategories.includes(cat.id)) continue;
        try {
          const res = await fetch(
            getApiEndpoint(`/api/properties/${prop.id}/${cat.endpoint}`),
            { headers }
          );
          if (!res.ok) continue;
          const data = await res.json();
          const items = Array.isArray(data) ? data : [];
          items.forEach(item => {
            allAssets.push({
              ...item,
              _propertyId: prop.id,
              _propertyName: prop.name || `Property ${prop.id}`,
              _propertyAddress: prop.address || '',
              _categoryId: cat.id,
              _categoryLabel: cat.label,
              _name: item.name || item.point_name || item.cradle_name || item.system_name || item.walkway_name || item.handrail_name || item.ladder_id || `${cat.label} ${item.id}`,
              _location: item.location_description || item.location || '—',
              _type: item.type || '—',
              _status: (item.status || 'Unknown').toLowerCase(),
              _lastInspection: item.last_inspection_date || item.lastTestDate || null,
              _nextInspection: item.next_inspection_date || item.nextTestDate || null,
              _isOverdue: isOverdue(item.next_inspection_date || item.nextTestDate),
              _isDueSoon: isDueSoon(item.next_inspection_date || item.nextTestDate),
            });
          });
        } catch {
          // skip silently
        }
      }
    }

    setAssets(allAssets);
    setLoading(false);
  };

  // Filter logic
  const filteredAssets = useMemo(() => {
    return assets.filter(a => {
      // Status filter
      if (statusFilter === 'overdue') {
        if (!a._isOverdue) return false;
      } else if (statusFilter !== 'all') {
        if (a._status !== statusFilter && !a._status.includes(statusFilter)) return false;
      }

      // Date range
      if (dateFrom) {
        const last = new Date(a._lastInspection);
        if (isNaN(last.getTime()) || last < new Date(dateFrom)) return false;
      }
      if (dateTo) {
        const last = new Date(a._lastInspection);
        if (isNaN(last.getTime()) || last > new Date(dateTo)) return false;
      }

      // Search
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const searchable = [a._name, a._location, a._type, a._categoryLabel, a._propertyName].join(' ').toLowerCase();
        if (!searchable.includes(q)) return false;
      }

      return true;
    });
  }, [assets, statusFilter, dateFrom, dateTo, searchQuery]);

  // Summary stats
  const stats = useMemo(() => {
    const total = filteredAssets.length;
    const pass = filteredAssets.filter(a => a._status === 'pass' || a._status === 'passed').length;
    const fail = filteredAssets.filter(a => a._status === 'fail' || a._status === 'failed').length;
    const pending = filteredAssets.filter(a => a._status === 'pending').length;
    const overdue = filteredAssets.filter(a => a._isOverdue).length;
    const dueSoon = filteredAssets.filter(a => a._isDueSoon).length;
    const compliance = total > 0 ? Math.round((pass / total) * 100) : 0;
    return { total, pass, fail, pending, overdue, dueSoon, compliance };
  }, [filteredAssets]);

  // Category toggle
  const toggleCategory = (catId) => {
    setSelectedCategories(prev =>
      prev.includes(catId) ? prev.filter(c => c !== catId) : [...prev, catId]
    );
  };

  const selectAllCategories = () => setSelectedCategories(ASSET_CATEGORIES.map(c => c.id));
  const clearAllCategories = () => setSelectedCategories([]);

  // Export handlers
  const handleExportCSV = () => {
    if (filteredAssets.length === 0) {
      toast.warning('No data to export');
      return;
    }
    const exportData = filteredAssets.map(a => ({
      'Property': a._propertyName,
      'Address': a._propertyAddress,
      'Category': a._categoryLabel,
      'Asset Name': a._name,
      'Location': a._location,
      'Type': a._type,
      'Status': a._status,
      'Last Inspection': formatDate(a._lastInspection),
      'Next Inspection': formatDate(a._nextInspection),
      'Overdue': a._isOverdue ? 'Yes' : 'No',
      'Notes': a.notes || '',
    }));
    try {
      exportToCSV(exportData, 'cloudsafe_compliance_report');
      toast.success('CSV exported');
    } catch (err) {
      toast.error('Export failed');
    }
    setShowExportMenu(false);
  };

  const handleExportPDF = () => {
    if (filteredAssets.length === 0) {
      toast.warning('No data to export');
      return;
    }
    const propName = selectedProperty === 'all'
      ? 'All Properties'
      : (properties.find(p => String(p.id) === String(selectedProperty))?.name || 'Property');
    const catLabel = selectedCategories.length === ASSET_CATEGORIES.length
      ? 'All Categories'
      : selectedCategories.map(c => ASSET_CATEGORIES.find(a => a.id === c)?.label).filter(Boolean).join(', ');

    const pdfData = filteredAssets.map(a => ({
      id: a.id,
      name: a._name,
      location: a._location,
      type: a._type,
      status: a._status,
      lastTestDate: a._lastInspection,
      nextTestDate: a._nextInspection,
    }));

    try {
      exportToPDF(pdfData, catLabel, propName, { status: statusFilter });
      toast.success('PDF exported');
    } catch (err) {
      toast.error('PDF export failed');
    }
    setShowExportMenu(false);
  };

  // Close export menu on outside click
  useEffect(() => {
    if (!showExportMenu) return;
    const handler = (e) => {
      if (!e.target.closest('.rpt-export-dropdown')) setShowExportMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showExportMenu]);

  // Status badge
  const statusBadge = (status, isOv) => {
    if (isOv) return <span className="rpt-badge rpt-badge-overdue">Overdue</span>;
    const s = (status || 'unknown').toLowerCase();
    if (s === 'pass' || s === 'passed') return <span className="rpt-badge rpt-badge-pass">Pass</span>;
    if (s === 'fail' || s === 'failed') return <span className="rpt-badge rpt-badge-fail">Fail</span>;
    if (s === 'pending') return <span className="rpt-badge rpt-badge-pending">Pending</span>;
    return <span className="rpt-badge rpt-badge-unknown">Unknown</span>;
  };

  const complianceColor = stats.compliance >= 80 ? 'var(--cs-green)' : stats.compliance >= 50 ? 'var(--cs-amber)' : 'var(--cs-red)';

  return (
    <div className="rpt-page">
      <MainHeader />
      <div className="rpt-container">
        <PageHeader
          title="Reports"
          subtitle="Compliance overview and asset reporting across all properties"
          icon={() => <FontAwesomeIcon icon={faChartBar} />}
          breadcrumbs={[
            { label: 'Dashboard', to: '/dashboard' },
            { label: 'Reports' }
          ]}
          actions={
            <div className="rpt-export-dropdown">
              <button
                className="rpt-btn rpt-btn-primary"
                onClick={() => setShowExportMenu(!showExportMenu)}
              >
                <FontAwesomeIcon icon={faFileExport} />
                <span>Export</span>
                <FontAwesomeIcon icon={faChevronDown} className="rpt-btn-caret" />
              </button>
              {showExportMenu && (
                <div className="rpt-export-menu">
                  <button onClick={handleExportCSV}>
                    <FontAwesomeIcon icon={faFileCsv} />
                    Export as CSV
                  </button>
                  <button onClick={handleExportPDF}>
                    <FontAwesomeIcon icon={faFilePdf} />
                    Export as PDF
                  </button>
                </div>
              )}
            </div>
          }
        />

        {/* ── Filter Bar ─────────────────────────────────── */}
        <div className="rpt-filters">
          <div className="rpt-filters-row">
            {/* Property selector */}
            <div className="rpt-filter-group">
              <label className="rpt-filter-label">
                <FontAwesomeIcon icon={faBuilding} /> Property
              </label>
              <select
                className="rpt-select"
                value={selectedProperty}
                onChange={(e) => setSelectedProperty(e.target.value)}
                disabled={propertiesLoading}
              >
                <option value="all">All Properties ({properties.length})</option>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            {/* Status filter */}
            <div className="rpt-filter-group">
              <label className="rpt-filter-label">
                <FontAwesomeIcon icon={faFilter} /> Status
              </label>
              <select
                className="rpt-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* Search */}
            <div className="rpt-filter-group rpt-filter-search">
              <label className="rpt-filter-label">
                <FontAwesomeIcon icon={faSearch} /> Search
              </label>
              <div className="rpt-search-wrap">
                <input
                  type="text"
                  className="rpt-input"
                  placeholder="Search assets, locations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button className="rpt-search-clear" onClick={() => setSearchQuery('')}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                )}
              </div>
            </div>

            {/* Filters + Refresh grouped */}
            <div className="rpt-filters-actions">
              <button
                className={`rpt-btn rpt-btn-ghost ${showFilters ? 'active' : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FontAwesomeIcon icon={faFilter} />
                <span>Filters</span>
                {(dateFrom || dateTo || selectedCategories.length < ASSET_CATEGORIES.length) && (
                  <span className="rpt-filter-badge">!</span>
                )}
              </button>
              <button
                className="rpt-btn rpt-btn-ghost"
                onClick={fetchAllAssets}
                disabled={loading}
              >
                <FontAwesomeIcon icon={faSync} spin={loading} />
              </button>
            </div>
          </div>

          {/* Advanced filters panel */}
          {showFilters && (
            <div className="rpt-filters-advanced">
              <div className="rpt-filters-advanced-row">
                {/* Date range */}
                <div className="rpt-filter-group">
                  <label className="rpt-filter-label">
                    <FontAwesomeIcon icon={faCalendarAlt} /> Last inspected from
                  </label>
                  <input type="date" className="rpt-input" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                </div>
                <div className="rpt-filter-group">
                  <label className="rpt-filter-label">
                    <FontAwesomeIcon icon={faCalendarAlt} /> to
                  </label>
                  <input type="date" className="rpt-input" value={dateTo} onChange={e => setDateTo(e.target.value)} />
                </div>
              </div>

              {/* Category pills */}
              <div className="rpt-cat-filter">
                <div className="rpt-cat-filter-header">
                  <span className="rpt-filter-label">Asset Categories</span>
                  <div className="rpt-cat-actions">
                    <button className="rpt-link" onClick={selectAllCategories}>Select all</button>
                    <button className="rpt-link" onClick={clearAllCategories}>Clear</button>
                  </div>
                </div>
                <div className="rpt-cat-pills">
                  {ASSET_CATEGORIES.map(cat => (
                    <button
                      key={cat.id}
                      className={`rpt-cat-pill ${selectedCategories.includes(cat.id) ? 'active' : ''}`}
                      onClick={() => toggleCategory(cat.id)}
                    >
                      <FontAwesomeIcon icon={cat.icon} />
                      <span>{cat.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Summary Cards ──────────────────────────────── */}
        <div className="rpt-stats">
          <div className="rpt-stat-card">
            <div className="rpt-stat-icon" style={{ background: 'var(--cs-blue-light)', color: 'var(--cs-blue)' }}>
              <FontAwesomeIcon icon={faClipboardList} />
            </div>
            <div className="rpt-stat-body">
              <span className="rpt-stat-value">{stats.total}</span>
              <span className="rpt-stat-label">Total Assets</span>
            </div>
          </div>

          <div className="rpt-stat-card">
            <div className="rpt-stat-icon" style={{ background: 'var(--cs-green-light)', color: 'var(--cs-green)' }}>
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
            <div className="rpt-stat-body">
              <span className="rpt-stat-value">{stats.pass}</span>
              <span className="rpt-stat-label">Passed</span>
            </div>
          </div>

          <div className="rpt-stat-card">
            <div className="rpt-stat-icon" style={{ background: 'var(--cs-red-light)', color: 'var(--cs-red)' }}>
              <FontAwesomeIcon icon={faTimesCircle} />
            </div>
            <div className="rpt-stat-body">
              <span className="rpt-stat-value">{stats.fail}</span>
              <span className="rpt-stat-label">Failed</span>
            </div>
          </div>

          <div className="rpt-stat-card">
            <div className="rpt-stat-icon" style={{ background: 'var(--cs-amber-light)', color: 'var(--cs-amber)' }}>
              <FontAwesomeIcon icon={faClock} />
            </div>
            <div className="rpt-stat-body">
              <span className="rpt-stat-value">{stats.pending}</span>
              <span className="rpt-stat-label">Pending</span>
            </div>
          </div>

          <div className="rpt-stat-card">
            <div className="rpt-stat-icon" style={{ background: 'var(--cs-red-light)', color: 'var(--cs-red-dark)' }}>
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </div>
            <div className="rpt-stat-body">
              <span className="rpt-stat-value">{stats.overdue}</span>
              <span className="rpt-stat-label">Overdue</span>
            </div>
          </div>

          <div className="rpt-stat-card rpt-stat-compliance">
            <div className="rpt-compliance-ring" style={{ '--pct': stats.compliance, '--clr': complianceColor }}>
              <span className="rpt-compliance-num">{stats.compliance}%</span>
            </div>
            <div className="rpt-stat-body">
              <span className="rpt-stat-label">Compliance Rate</span>
            </div>
          </div>
        </div>

        {/* ── Results Table ──────────────────────────────── */}
        <div className="rpt-table-wrap">
          {loading ? (
            <div className="rpt-loading">
              <FontAwesomeIcon icon={faSync} spin />
              <p>Loading asset data across {selectedProperty === 'all' ? properties.length : 1} {selectedProperty === 'all' ? 'properties' : 'property'}...</p>
            </div>
          ) : filteredAssets.length === 0 ? (
            <div className="rpt-empty">
              <FontAwesomeIcon icon={faClipboardList} />
              <h3>No assets found</h3>
              <p>Try adjusting your filters or select a different property.</p>
            </div>
          ) : (
            <>
              <div className="rpt-table-header">
                <span className="rpt-results-count">{filteredAssets.length} asset{filteredAssets.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="rpt-table-scroll">
                <table className="rpt-table">
                  <thead>
                    <tr>
                      <th>Property</th>
                      <th>Category</th>
                      <th>Asset</th>
                      <th>Location</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Last Inspected</th>
                      <th>Next Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAssets.map((a, i) => (
                      <tr key={`${a._categoryId}-${a.id}-${i}`} className={a._isOverdue ? 'rpt-row-overdue' : ''}>
                        <td className="rpt-td-prop">
                          <span className="rpt-prop-name">{a._propertyName}</span>
                        </td>
                        <td>
                          <span className="rpt-cat-tag">{a._categoryLabel}</span>
                        </td>
                        <td className="rpt-td-name">{a._name}</td>
                        <td>{a._location}</td>
                        <td>{a._type}</td>
                        <td>{statusBadge(a._status, a._isOverdue)}</td>
                        <td>{formatDate(a._lastInspection)}</td>
                        <td className={a._isOverdue ? 'rpt-cell-overdue' : a._isDueSoon ? 'rpt-cell-due' : ''}>
                          {formatDate(a._nextInspection)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
